export interface RoleOption {
  value: string;
  label: string;
  primary: boolean;
  icon?: string;
  description?: string;
  category?: string;
}

export const ROLE_OPTIONS: RoleOption[] = [
  // Core Oncology Team (Issue #17)
  {
    value: 'medical-oncologist',
    label: 'Medical Oncologist',
    primary: true,
    icon: 'heart',
    description: 'Medical cancer treatment specialist',
    category: 'Core Oncology Team'
  },
  {
    value: 'radiation-oncologist',
    label: 'Radiation Oncologist',
    primary: true,
    icon: 'zap',
    description: 'Radiation therapy specialist',
    category: 'Core Oncology Team'
  },
  {
    value: 'surgical-oncologist',
    label: 'Surgical Oncologist/Cancer Surgeon',
    primary: true,
    icon: 'scissors',
    description: 'Cancer surgery specialist',
    category: 'Core Oncology Team'
  },
  {
    value: 'oncology-nurse',
    label: 'Oncology Nurse / Clinical Nurse Specialist',
    primary: true,
    icon: 'shield-check',
    description: 'Specialized oncology nursing care',
    category: 'Core Oncology Team'
  },
  {
    value: 'tumor-board-coordinator',
    label: 'Tumor Board Coordinator/Navigator',
    primary: true,
    icon: 'users',
    description: 'Coordinates multidisciplinary tumor board',
    category: 'Core Oncology Team'
  },
  {
    value: 'radiologist',
    label: 'Radiologist',
    primary: true,
    icon: 'brain',
    description: 'Medical imaging specialist',
    category: 'Core Oncology Team'
  },
  {
    value: 'pathologist',
    label: 'Pathologist',
    primary: true,
    icon: 'microscope',
    description: 'Disease diagnosis specialist',
    category: 'Core Oncology Team'
  },

  // Nursing and Physician Assistants (Issue #18.d)
  {
    value: 'oncology-nurse-on',
    label: 'ON – Oncology Nurse',
    primary: false,
    icon: 'shield-check',
    description: 'Oncology nursing specialist',
    category: 'Nursing and Physician Assistants'
  },
  {
    value: 'oncology-nurse-navigator',
    label: 'ONN – Oncology Nurse Navigator',
    primary: false,
    icon: 'shield-check',
    description: 'Patient navigation specialist',
    category: 'Nursing and Physician Assistants'
  },
  {
    value: 'nurse-practitioner',
    label: 'NP - Nurse Practitioner',
    primary: false,
    icon: 'shield-check',
    description: 'Advanced practice nurse',
    category: 'Nursing and Physician Assistants'
  },
  {
    value: 'physician-assistant',
    label: 'Physician Assistant',
    primary: false,
    icon: 'stethoscope',
    description: 'Physician assistant in oncology',
    category: 'Nursing and Physician Assistants'
  },

  // Surgical Subspecialists (Issue #18.e)
  {
    value: 'general-surgeon',
    label: 'General Surgeon',
    primary: false,
    icon: 'scissors',
    description: 'General surgical specialist',
    category: 'Surgical Subspecialists'
  },
  {
    value: 'thoracic-surgeon',
    label: 'Thoracic Surgeon',
    primary: false,
    icon: 'scissors',
    description: 'Chest surgery specialist',
    category: 'Surgical Subspecialists'
  },
  {
    value: 'gynecologic-oncologist',
    label: 'Gynecologic Oncologist',
    primary: false,
    icon: 'scissors',
    description: 'Female reproductive cancer specialist',
    category: 'Surgical Subspecialists'
  },
  {
    value: 'urologic-oncologist',
    label: 'Urologic Oncologist',
    primary: false,
    icon: 'scissors',
    description: 'Urological cancer specialist',
    category: 'Surgical Subspecialists'
  },
  {
    value: 'head-neck-surgeon',
    label: 'Head & Neck (ENT) Surgeon',
    primary: false,
    icon: 'scissors',
    description: 'Head and neck surgery specialist',
    category: 'Surgical Subspecialists'
  },
  {
    value: 'orthopedic-oncologist',
    label: 'Orthopedic Oncologist',
    primary: false,
    icon: 'scissors',
    description: 'Bone and soft tissue tumor specialist',
    category: 'Surgical Subspecialists'
  },
  {
    value: 'plastic-reconstructive-surgeon',
    label: 'Plastic/Reconstructive Surgeon',
    primary: false,
    icon: 'scissors',
    description: 'Reconstructive surgery specialist',
    category: 'Surgical Subspecialists'
  },

  // Diagnostic & Imaging (Issue #18.f)
  {
    value: 'diagnostic-radiologist',
    label: 'Diagnostic Radiologist',
    primary: false,
    icon: 'brain',
    description: 'Diagnostic imaging specialist',
    category: 'Diagnostic & Imaging'
  },
  {
    value: 'nuclear-medicine-specialist',
    label: 'Nuclear Medicine / PET Specialist',
    primary: false,
    icon: 'brain',
    description: 'Nuclear imaging specialist',
    category: 'Diagnostic & Imaging'
  },
  {
    value: 'interventional-radiologist',
    label: 'Interventional Radiologist',
    primary: false,
    icon: 'brain',
    description: 'Minimally invasive procedure specialist',
    category: 'Diagnostic & Imaging'
  },
  {
    value: 'pathologist-anatomic',
    label: 'Pathologist (Anatomic)',
    primary: false,
    icon: 'microscope',
    description: 'Tissue diagnosis specialist',
    category: 'Diagnostic & Imaging'
  },
  {
    value: 'pathologist-molecular',
    label: 'Pathologist (Molecular)',
    primary: false,
    icon: 'test-tube',
    description: 'Molecular diagnosis specialist',
    category: 'Diagnostic & Imaging'
  },
  {
    value: 'clinical-laboratory-specialist',
    label: 'Clinical Laboratory Specialist',
    primary: false,
    icon: 'test-tube',
    description: 'Laboratory testing specialist',
    category: 'Diagnostic & Imaging'
  },

  // Supportive & Symptom Management (Issue #18.g)
  {
    value: 'palliative-care-specialist',
    label: 'Palliative Care Specialist',
    primary: false,
    icon: 'heart',
    description: 'Symptom management and comfort care',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'pain-medicine-anesthesiologist',
    label: 'Pain Medicine/Anesthesiology',
    primary: false,
    icon: 'pill',
    description: 'Pain management specialist',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'radiation-therapist',
    label: 'Radiation Therapist/Dosimetrist/Medical Physicist',
    primary: false,
    icon: 'zap',
    description: 'Radiation therapy technical specialist',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'oncology-pharmacist',
    label: 'Oncology Pharmacist',
    primary: false,
    icon: 'pill',
    description: 'Cancer medication specialist',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'nutritionist-dietitian',
    label: 'Nutritionist/Dietitian',
    primary: false,
    icon: 'heart',
    description: 'Nutrition and diet specialist',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'physical-occupational-therapist',
    label: 'Physical Therapist / Occupational Therapist',
    primary: false,
    icon: 'activity',
    description: 'Rehabilitation specialist',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'speech-swallow-therapist',
    label: 'Speech & Swallow Therapist',
    primary: false,
    icon: 'activity',
    description: 'Speech and swallowing specialist',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'wound-ostomy-nurse',
    label: 'Wound/Ostomy Nurse',
    primary: false,
    icon: 'shield-check',
    description: 'Wound and ostomy care specialist',
    category: 'Supportive & Symptom Management'
  },
  {
    value: 'infectious-disease-specialist',
    label: 'Infectious Disease Specialist',
    primary: false,
    icon: 'stethoscope',
    description: 'Infection prevention and treatment',
    category: 'Supportive & Symptom Management'
  },

  // Specialty Consultants (Issue #19)
  {
    value: 'family-physician-pcp',
    label: 'Family Physician (PCP)',
    primary: false,
    icon: 'stethoscope',
    description: 'Primary care physician',
    category: 'Specialty Consultants'
  },
  {
    value: 'internal-medicine',
    label: 'Internal Medicine',
    primary: false,
    icon: 'stethoscope',
    description: 'Internal medicine specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'cardio-oncologist',
    label: 'Cardio-oncologist (Cardiologist)',
    primary: false,
    icon: 'heart',
    description: 'Heart health in cancer patients',
    category: 'Specialty Consultants'
  },
  {
    value: 'nephrologist',
    label: 'Nephrologist',
    primary: false,
    icon: 'stethoscope',
    description: 'Kidney specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'endocrinologist',
    label: 'Endocrinologist',
    primary: false,
    icon: 'stethoscope',
    description: 'Hormone and metabolism specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'pulmonologist',
    label: 'Pulmonologist',
    primary: false,
    icon: 'stethoscope',
    description: 'Lung specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'dermatologist',
    label: 'Dermatologist',
    primary: false,
    icon: 'stethoscope',
    description: 'Skin specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'fertility-specialist',
    label: 'Fertility Specialist / Reproductive Endocrinologist',
    primary: false,
    icon: 'stethoscope',
    description: 'Fertility preservation specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'genetic-counselor',
    label: 'Genetic Counselor / Medical Geneticist',
    primary: false,
    icon: 'test-tube',
    description: 'Genetic risk assessment specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'psychiatrist-psycho-oncologist',
    label: 'Psychiatrist / Psycho-oncologist / Psychologist',
    primary: false,
    icon: 'brain',
    description: 'Mental health specialist',
    category: 'Specialty Consultants'
  },
  {
    value: 'social-worker-case-manager',
    label: 'Social Worker / Case Manager',
    primary: false,
    icon: 'users',
    description: 'Patient support and resources',
    category: 'Specialty Consultants'
  },
  {
    value: 'speech-language-pathologist',
    label: 'Speech-Language Pathologist',
    primary: false,
    icon: 'activity',
    description: 'Communication and swallowing specialist',
    category: 'Specialty Consultants'
  },

  // Research, Admin & Systems (Issue #20)
  {
    value: 'clinical-trial-coordinator',
    label: 'Clinical Trial Coordinator/Research Nurse',
    primary: false,
    icon: 'flask-conical',
    description: 'Clinical trials management',
    category: 'Research, Admin & Systems'
  },
  {
    value: 'bioinformatician',
    label: 'Bioinformatician / Molecular Tumor Board Specialist',
    primary: false,
    icon: 'test-tube',
    description: 'Genomic data analysis specialist',
    category: 'Research, Admin & Systems'
  },
  {
    value: 'health-economics-specialist',
    label: 'Health-Economics / Reimbursement Specialist / Financial Counselor',
    primary: false,
    icon: 'briefcase',
    description: 'Healthcare finance specialist',
    category: 'Research, Admin & Systems'
  },
  {
    value: 'payer-liaison',
    label: 'Payer Liaison/Utilization Review',
    primary: false,
    icon: 'briefcase',
    description: 'Insurance and authorization specialist',
    category: 'Research, Admin & Systems'
  },
  {
    value: 'patient-advocate-coordinator',
    label: 'Patient Advocate/Survivorship Coordinator',
    primary: false,
    icon: 'user-check',
    description: 'Patient advocacy and survivorship',
    category: 'Research, Admin & Systems'
  },

  // Patient and Other
  {
    value: 'patient',
    label: 'Patient',
    primary: true,
    icon: 'user-check',
    description: 'Receiving cancer care',
    category: 'Patient'
  },
  {
    value: 'family-caregiver',
    label: 'Family Member/Caregiver',
    primary: false,
    icon: 'heart',
    description: 'Patient family or caregiver',
    category: 'Patient'
  },
  {
    value: 'other',
    label: 'Other',
    primary: false,
    icon: 'user-check',
    description: 'Other healthcare role',
    category: 'Other'
  }
];
